<script>
import { preventDefault } from "svelte/legacy";
import {onMount} from "svelte";
import { projectState } from "@app/store/projectStore.svelte.js";
import { eventGroupState } from "@app/store/eventGroupStore.svelte.js";
import { environment } from "@app/store/envStore.svelte.js";
import { modalState, hideModal } from "@app/store/modalStore.svelte.js";
import { toast } from "@zerodevx/svelte-toast";
import { styleState } from "@app/store/styleStore.svelte.js";
import { authState } from "@app/store/authStore.svelte.js";
import { shouldWatermark, allowedSubtitleExts } from "@app/lib/entitlements.js";
import { warnIfWatermarked } from "@app/lib/upgradePrompt.js";
import { fade } from "svelte/transition";
import { saveAs } from "file-saver";
import { Circle } from "svelte-loading-spinners";
import Swal from "sweetalert2";
import { SWAL_BASE, SWAL_CLASSES } from "@app/lib/swalClasses.js";
import { activateSubscription } from "@app/lib/activateSubscription.js";
import * as m from "@app/paraglide/messages.js";
/* Firebase */
import { functions } from "@app/configs/firebase.js";
import storage from "@app/configs/storage.js";
import { ref, uploadBytes, uploadString, getDownloadURL } from "firebase/storage";
import { httpsCallable } from "firebase/functions";

/* CC LIB */
import encode from "@cclib/functions/encode.js";
import defaults from "@cclib/lib/defaults.js";
import encodings from "@cclib/dict/encodings.js";
import formats from "@cclib/lib/formats.js";
import frameRates from "@cclib/dict/frameRates.js";
import tcLib from "@cclib/lib/timecode.js";
import incodeTime from "@cclib/lib/incodeTime.js";
import _Event from "@cclib/classes/event.js";
import offset from "@cclib/functions/events/tcOffset.js";
import tcMultiply from "@cclib/functions/special/tcMultiply.js";
import orderByTime from "@cclib/functions/eventGroups/orderByTime.js";
import removeHtmlEntities from "@cclib/functions/eventGroups/removeHtmlEntities.js";
import replaceTrialText from "@cclib/functions/eventGroups/replaceTrialText.js";
import reorderRtlText from "@cclib/functions/utility/reorderRtlText.js";
import wrapStyledTextWithSpan from "@cclib/functions/quill/wrapStyledTextWithSpan.js";
import syncEncodeLanguageFromEventGroup from "@cclib/functions/helpers/syncEncodeLanguageFromEventGroup.js";
import contentAdvisoryInformation from "@cclib/dict/contentAdvisoryInformation.js";
import vChipRatings from "@cclib/dict/vChipRatings.js";

/* Preset Store */
import {
    createPreset,
    getPresetsByType,
    getPresetById,
    PRESET_TYPES
} from "@app/store/presetStore.svelte.js";

let subtitleExportDefaults = JSON.parse(localStorage.getItem("cc-subtitle-export-defaults")) || {};

const TRACK_LETTERS = ["A", "B", "C", "D"];
const ALL_TRACK_OPTIONS = ["none", ...TRACK_LETTERS];

/* Filter events based on the selected export tracks. All events export when every track is selected (default). */
function filterEventsByTrack(events, selectedTracks) {
    if (
        selectedTracks.length === 0 ||
        ALL_TRACK_OPTIONS.every((option) => selectedTracks.includes(option))
    ) {
        return events;
    }

    return events.filter((event) => {
        const eventTrack = TRACK_LETTERS.includes(event.track)
            ? event.track
            : "none";
        return selectedTracks.includes(eventTrack);
    });
}

// Must match v8EncodeMxf timeoutSeconds in functions/index.js. SDK option is `timeout` (ms), not `timeoutSeconds`.
const ENCODE_MXF_CALLABLE_TIMEOUT_MS = 1_800_000;

const xmlProfiles = [
    "Timed-Text Markup Language",
    "Verizon TTML",
    "HBO GO TTML",
    "HBO MAX SMPTE",
    "HBO SMPTE TTML",
    "TTML Legacy",
    "SMPTE TTML",
    "IMSC Text Captions",
    "IMSC Text Subtitles",
    "IMSC Text Captions - JA",
    "Ooona TTML",
    "IMSC 1.0 Basic",
    "TTML FooEngine",
    "Timed-Text Markup Language",
    "Distribution Format Exchange Profile (DFXP)",
    "DLP Cinema",
    "Netflix TT Captions",
    "Netflix TT Subtitles",
    "MAGIC TT",
    "EBU TT",
];

let eventGroup = $state(
        getSelectedEventGroup([
            "subtitle",
            "translation",
            "audio description",
            "audio dubbing",
            "transcription",
        ]),
    ),
    trgExt = $state(subtitleExportDefaults.trgExt || "srt"),
    trgProfile = $state(
        subtitleExportDefaults.trgProfile || "SubRip Video Subtitle Script",
    ),
    trgFrameRate = $state(projectState.frameRate),
    trgDropFrame = $state(projectState.dropFrame ? "true" : "false"),
    tcOffset = $state("00:00:00:00"),
    tcOffsetType = $state("add"),
    tcMultiplier = $state(1),
    showOptions = $state(false),
    fileEncoding = $state(defaults.encoding),
    fns = $state("include"),
    exportTracks = $state(["none", "A", "B", "C", "D"]),
    reverseRtlStartEndOption = $state(false),
    repositionToTop = $state(false),
    ignoreAll = false,
    cc1EncodeTarget = $state(),
    cc2EncodeTarget = $state("None"),
    cc3EncodeTarget = $state("None"),
    cc4EncodeTarget = $state("None"),
    programAEncodeTarget = $state(),
    programBEncodeTarget = $state("None"),
    programCEncodeTarget = $state("None"),
    programDEncodeTarget = $state("None"),
    programEEncodeTarget = $state("None"),
    programFEncodeTarget = $state("None"),
    duration = 0,
    exporting = $state(false),
    aafEmbed = $state("aaf-sc"),
    mxfEmbed = $state(false),
    showProgramInfoOptions = $state(false),
    show608Options = $state(true),
    show708Options = $state(true),
    enableProgramInfo = $state(false),
    programLengthHours = $state(0),
    programLengthMinutes = $state(0),
    programName = $state(""),
    basicKeywordGroup = $state("education"),
    showVChipOptions = $state(false),
    enableVChipInfo = $state(false),
    detailKeywordGroup = $state([]),
    vChipType = $state("TV Parental Guideline"),
    vChipRating = $state("N/A"),
    vChipContent = $state([]),
    trgIncode = $state(incodeTime.mediaSecondsToIncodeLabel(projectState.incode, projectState.frameRate, projectState.dropFrame)),
    useClockTime = $state('yes'), //use clock time for trgIncode
    // Preset-related state
    availablePresets = $derived(getPresetsByType(PRESET_TYPES.SUBTITLE)),
    selectedPresetId = $state(null);


function syncLanguageCodeFromEventGroup() {
    syncEncodeLanguageFromEventGroup(
        eventGroup,
        trgProfile,
        eventGroupState,
        formats.profileMapping,
    );
}

onMount(() =>{
    useClockTime = formats.clockTimeFormats.indexOf(trgProfile) > -1 ? 'no' : 'yes';
    syncLanguageCodeFromEventGroup();
    // A saved default the plan no longer includes (e.g. SCC on Free) falls back to SRT.
    if (!allowedSubtitleExts([trgExt]).length) {
        trgExt = "srt";
        updateProfile();
    }
    warnIfWatermarked(trgExt, showTrialWarning);
})

// Function to save current settings as a preset
async function saveAsPreset() {
    const { value: presetName } = await Swal.fire({
        ...SWAL_BASE,
        titleText: m.subtitle_export_preset_save_title(),
        input: 'text',
        inputLabel: m.subtitle_export_preset_name_label(),
        inputPlaceholder: m.subtitle_export_preset_name_placeholder(),
        showCancelButton: true,
        confirmButtonText: m.subtitle_export_preset_save_confirm(),
        cancelButtonText: m.subtitle_export_preset_save_cancel(),
        customClass: SWAL_CLASSES.confirmCancel,
        inputValidator: (value) => {
            if (!value) {
                return m.subtitle_export_preset_name_required();
            }
        }
    });

    if (presetName) {
        try {
            /* We can't save undefined values in the config that is saved to Firebase. Clear them out */
            
            // Serialize encode options if they exist
            let encodeOptions = null;
            if (formats.profileMapping[trgProfile]?.options?.encode?.length > 0) {
                encodeOptions = formats.profileMapping[trgProfile].options.encode.map(option => ({
                    name: option.name,
                    selected: option.selected,
                    type: option.type,
                    values: option.values
                }));
            }
            
            const config = {
                trgExt,
                trgProfile,
                trgFrameRate,
                trgDropFrame,
                tcOffset,
                tcOffsetType,
                trgIncode,
                tcMultiplier,
                fileEncoding,
                fns,
                exportTracks,
                reverseRtlStartEndOption,
                repositionToTop,
                cc1EncodeTarget,
                cc2EncodeTarget,
                cc3EncodeTarget,
                cc4EncodeTarget,
                programAEncodeTarget,
                programBEncodeTarget,
                programCEncodeTarget,
                programDEncodeTarget,
                programEEncodeTarget,
                programFEncodeTarget,
                aafEmbed,
                mxfEmbed,
                showProgramInfoOptions,
                show608Options,
                show708Options,
                enableProgramInfo,
                programLengthHours,
                programLengthMinutes,
                programName,
                basicKeywordGroup,
                showVChipOptions,
                enableVChipInfo,
                detailKeywordGroup,
                vChipType,
                vChipRating,
                vChipContent,
                useClockTime,
                encodeOptions
            };

            Object.keys(config).forEach(key => {
                if (config[key] === undefined) {
                    delete config[key];
                }
            });

            // The preset store resolves the Firebase auth uid itself so that
            // saves land in the same collection loadPresets reads from.
            const preset = await createPreset({
                name: presetName,
                type: PRESET_TYPES.SUBTITLE,
                config
            });

            toast.push(m.subtitle_export_preset_saved({ name: presetName }), { classes: ['toast-success'] });
            selectedPresetId = preset.id;
        } catch (error) {
            console.error("Error saving preset:", error);
            toast.push(m.subtitle_export_preset_save_failed({ error: error.message }), { classes: ['toast-danger'] });
        }
    }
}

// Function to show load preset dialog
async function showLoadPresetDialog() {
    if (availablePresets.length === 0) {
        Swal.fire({
            ...SWAL_BASE,
            titleText: m.subtitle_export_presets_none_title(),
            text: m.subtitle_export_presets_none_text(),
            icon: 'info',
            confirmButtonText: m.subtitle_export_ok(),
            customClass: SWAL_CLASSES.ok,
        });
        return;
    }

    // Create options for the select dropdown
    const presetOptions = {};
    availablePresets.forEach(preset => {
        presetOptions[preset.id] = preset.name;
    });

    const { value: selectedId } = await Swal.fire({
        ...SWAL_BASE,
        titleText: m.subtitle_export_preset_load_title(),
        input: 'select',
        inputLabel: m.subtitle_export_preset_load_label(),
        inputOptions: presetOptions,
        inputPlaceholder: m.subtitle_export_preset_load_placeholder(),
        showCancelButton: true,
        confirmButtonText: m.subtitle_export_preset_load_confirm(),
        cancelButtonText: m.subtitle_export_preset_load_cancel(),
        customClass: SWAL_CLASSES.confirmCancel,
        inputValidator: (value) => {
            if (!value) {
                return m.subtitle_export_preset_load_required();
            }
        }
    });

    if (selectedId) {
        loadPreset(selectedId);
    }
}

// Function to load a preset
function loadPreset(presetId) {
    if (!presetId) return;

    const preset = getPresetById(presetId);
    if (!preset || !preset.config) {
        toast.push(m.subtitle_export_preset_not_found(), { classes: ['toast-warning'] });
        return;
    }

    const config = preset.config;
    
    // Apply all config values to state
    trgExt = config.trgExt || trgExt;
    trgProfile = config.trgProfile || trgProfile;
    trgFrameRate = config.trgFrameRate || trgFrameRate;
    trgDropFrame = config.trgDropFrame || trgDropFrame;
    tcOffset = config.tcOffset || tcOffset;
    tcOffsetType = config.tcOffsetType || tcOffsetType;
    trgIncode = config.trgIncode || trgIncode;
    tcMultiplier = config.tcMultiplier || tcMultiplier;
    fileEncoding = config.fileEncoding || fileEncoding;
    fns = config.fns || fns;
    exportTracks = Array.isArray(config.exportTracks) && config.exportTracks.length > 0 ? config.exportTracks : exportTracks;
    reverseRtlStartEndOption = config.reverseRtlStartEndOption !== undefined ? config.reverseRtlStartEndOption : reverseRtlStartEndOption;
    repositionToTop = config.repositionToTop !== undefined ? config.repositionToTop : repositionToTop;
    cc1EncodeTarget = config.cc1EncodeTarget || cc1EncodeTarget;
    cc2EncodeTarget = config.cc2EncodeTarget || cc2EncodeTarget;
    cc3EncodeTarget = config.cc3EncodeTarget || cc3EncodeTarget;
    cc4EncodeTarget = config.cc4EncodeTarget || cc4EncodeTarget;
    programAEncodeTarget = config.programAEncodeTarget || programAEncodeTarget;
    programBEncodeTarget = config.programBEncodeTarget || programBEncodeTarget;
    programCEncodeTarget = config.programCEncodeTarget || programCEncodeTarget;
    programDEncodeTarget = config.programDEncodeTarget || programDEncodeTarget;
    programEEncodeTarget = config.programEEncodeTarget || programEEncodeTarget;
    programFEncodeTarget = config.programFEncodeTarget || programFEncodeTarget;
    aafEmbed = config.aafEmbed || aafEmbed;
    mxfEmbed = config.mxfEmbed !== undefined ? config.mxfEmbed : mxfEmbed;
    showProgramInfoOptions = config.showProgramInfoOptions !== undefined ? config.showProgramInfoOptions : showProgramInfoOptions;
    show608Options = config.show608Options !== undefined ? config.show608Options : show608Options;
    show708Options = config.show708Options !== undefined ? config.show708Options : show708Options;
    enableProgramInfo = config.enableProgramInfo !== undefined ? config.enableProgramInfo : enableProgramInfo;
    programLengthHours = config.programLengthHours || programLengthHours;
    programLengthMinutes = config.programLengthMinutes || programLengthMinutes;
    programName = config.programName || programName;
    basicKeywordGroup = config.basicKeywordGroup || basicKeywordGroup;
    showVChipOptions = config.showVChipOptions !== undefined ? config.showVChipOptions : showVChipOptions;
    enableVChipInfo = config.enableVChipInfo !== undefined ? config.enableVChipInfo : enableVChipInfo;
    detailKeywordGroup = config.detailKeywordGroup || detailKeywordGroup;
    vChipType = config.vChipType || vChipType;
    vChipRating = config.vChipRating || vChipRating;
    vChipContent = config.vChipContent || vChipContent;
    useClockTime = config.useClockTime || useClockTime;
    
    // Restore encode options if they exist in the preset
    if (config.encodeOptions && Array.isArray(config.encodeOptions)) {
        // Get the current encode options for the loaded profile
        const currentEncodeOptions = formats.profileMapping[trgProfile]?.options?.encode;
        
        if (currentEncodeOptions && currentEncodeOptions.length > 0) {
            // Match saved options with current options by name and restore selected values
            config.encodeOptions.forEach(savedOption => {
                const currentOption = currentEncodeOptions.find(opt => opt.name === savedOption.name);
                if (currentOption) {
                    currentOption.selected = savedOption.selected;
                }
            });
        }
    }
    
    updateUseClockTime();
    
    selectedPresetId = presetId;
    toast.push(m.subtitle_export_preset_loaded({ name: preset.name }), { classes: ['toast-success'] });
}

function showTrialWarning() {
    Swal.fire({
        ...SWAL_BASE,
        titleText: m.subtitle_export_trial_title(),
        text: m.subtitle_export_trial_text(),
        showDenyButton: true,
        showCancelButton: false,
        confirmButtonText: m.subtitle_export_trial_activate(),
        denyButtonText: m.subtitle_export_trial_continue(),
        allowOutsideClick: false,
        allowEscapeKey: false,
        customClass: SWAL_CLASSES.trial,
    })
        .then((result) => {
            if (result.isConfirmed) {
                activateSubscription();
            }
        })
        .catch((err) => {
            console.log(err);
            console.log(err.message);
        });
}

function getSelectedEventGroup(validTypes) {
    let evGroup = eventGroupState[projectState.selected];
    if (evGroup) {
        if (validTypes.indexOf(evGroup.type) > -1) {
            return evGroup.id;
        } else {
            return false;
        }
    } else {
        return false;
    }
}

function validateTc(e) {
    tcOffset = tcLib.tcValidate(
        e.target.value,
        parseFloat(trgFrameRate),
        trgDropFrame === "true" ? true : false,
    );
}

function validateTrgIncode(e){
    trgIncode = tcLib.tcValidate(
        e.target.value,
        parseFloat(trgFrameRate),
        trgDropFrame === "true" ? true : false,
    );
}

function updateProfile() {
    trgProfile = formats.trgProfiles[trgExt][0];
    trgFrameRate = projectState.frameRate;
    trgDropFrame = projectState.dropFrame ? "true" : "false";

    updateUseClockTime();
    syncLanguageCodeFromEventGroup();
}

function updateUseClockTime(){
    if (trgProfile && formats.clockTimeFormats.indexOf(trgProfile) > -1){
        useClockTime = 'no';
    } else {
        useClockTime = 'yes';
    }
}

async function cancelExport(eventGroupId, eventId) {
    toast.push(m.subtitle_export_aborted_toast(), {
        classes: ["toast-warning"],
    });

    projectState.selected = eventGroupId;
    eventGroupState[projectState.selected].selected = [eventId];
    setTimeout(() => {
        const selectedIndex =
            eventGroupState[projectState.selected].selected[0];
        window.dispatchEvent(
            new CustomEvent("scrollToVirtualListItem", {
                detail: { index: selectedIndex },
            }),
        );
    }, 250);

    hideModal();
}

function alertUser(msg) {
    let response = Swal.fire({
        ...SWAL_BASE,
        titleText: m.subtitle_export_precheck_title(),
        text: msg,
        showDenyButton: true,
        showCancelButton: true,
        confirmButtonText: m.subtitle_export_precheck_abort(),
        denyButtonText: m.subtitle_export_precheck_ignore(),
        cancelButtonText: m.subtitle_export_precheck_ignore_all(),
        allowOutsideClick: false,
        allowEscapeKey: false,
        customClass: SWAL_CLASSES.precheck,
    }).then((result) => {
        if (result.isConfirmed) {
            return true;
        } else if (result.isDenied) {
            return false;
        } else if (result.isDismissed) {
            ignoreAll = true;
            return false;
        }
    });

    return response;
}

async function preExportCheck(events) {
    for (let i = 0; i < events.length; i++) {
        if (!ignoreAll) {
            if (events[i].text) {
                if (isNaN(events[i].start)) {
                    if (
                        await alertUser(
                            m.subtitle_export_event_missing_start({ index: i + 1 }),
                        )
                    ) {
                        return {
                            err: true,
                            eventId: i,
                        };
                    }
                } else if (isNaN(events[i].end)) {
                    if (
                        await alertUser(
                            m.subtitle_export_event_missing_end({ index: i + 1 }),
                        )
                    ) {
                        return {
                            err: true,
                            eventId: i,
                        };
                    }
                } else if (events[i].start === events[i].end) {
                    if (
                        await alertUser(
                            m.subtitle_export_event_same_time({ index: i + 1 }),
                        )
                    ) {
                        return {
                            err: true,
                            eventId: i,
                        };
                    }
                } else if (events[i].start > events[i].end) {
                    if (
                        await alertUser(
                            m.subtitle_export_event_start_after_end({ index: i + 1 }),
                        )
                    ) {
                        return {
                            err: true,
                            eventId: i,
                        };
                    }
                } else if (i > 0 && events[i].start < events[i - 1].end) {
                    /* Adding check in case overlap is less than a frame */
                    if (
                        events[i - 1].end - events[i].start <
                        1 / projectState.frameRate
                    ) {
                        events[i].start = events[i - 1].end;
                    } else if (
                        await alertUser(
                            m.subtitle_export_event_overlap({ index: i + 1 }),
                        )
                    ) {
                        return {
                            err: true,
                            eventId: i,
                        };
                    }
                }
            } else {
                if (
                    await alertUser(
                        m.subtitle_export_event_missing_text({ index: i + 1 }),
                    )
                ) {
                    return {
                        err: true,
                        eventId: i,
                    };
                }
            }
        }
    }

    return {
        err: false,
    };
}

async function exportSubtitles() {
    exporting = true;
    toast.push(m.subtitle_export_encoding_toast(), { classes: ["toast-info"] });
    try {
        let options,
            output,
            defaultEncoding =
                formats.defaultEncodingMap[
                    formats.profileMapping[trgProfile].name
                ],
            eventGroupIndex,
            fileName,
            fileBlob,
            copyOfEventGroup;

        trgFrameRate = parseFloat(trgFrameRate);
        if ([23.976,24,25,30,50,60].indexOf(trgFrameRate) > -1){
            trgDropFrame = false;
        } else {
            trgDropFrame = trgDropFrame === "true" ? true : false;
        }
        
        trgIncode = tcLib.tcValidate(
            trgIncode,
            trgFrameRate,
            trgDropFrame,
        );

        tcOffset = tcLib.tcValidate(
            tcOffset,
            trgFrameRate,
            trgDropFrame,
        );

        /* Incode Math */
        let incode = trgIncode;

        if (useClockTime === "no"){
            incode = incodeTime.remapIncodeForNonClockPipeline(trgIncode, trgFrameRate, trgDropFrame);
        }
        /* Incode Math End */

        eventGroupIndex = eventGroupState.findIndex((group) => {
            return group.id === eventGroup;
        });

        fileName = eventGroupState[eventGroupIndex].name + "." + trgExt;
        copyOfEventGroup = JSON.parse(
            JSON.stringify(eventGroupState[eventGroupIndex]),
        );

        /* Reposition to Top */
        if (repositionToTop) {
            copyOfEventGroup.events.forEach((event) => {
                event.xPos = "center";
                event.yPos = "start";
            });
        }
        /* Reposition to Top END */

        /* FS Filtering START */
        if (fns === "exclude") {
            copyOfEventGroup.events = copyOfEventGroup.events.filter(
                (event) => {
                    return !event.forced;
                },
            );
        } else if (fns === "only") {
            copyOfEventGroup.events = copyOfEventGroup.events.filter(
                (event) => {
                    return event.forced;
                },
            );
        }
        /* FS Filtering END */

        /* Track Filtering START */
        copyOfEventGroup.events = filterEventsByTrack(
            copyOfEventGroup.events,
            exportTracks,
        );
        /* Track Filtering END */

        /* RTL Formatting */
        if (reverseRtlStartEndOption) {
            copyOfEventGroup = reorderRtlText(copyOfEventGroup);
        }
        /* RTL Formatting END */

        copyOfEventGroup = orderByTime(copyOfEventGroup);
        copyOfEventGroup = removeHtmlEntities(copyOfEventGroup);
        let preCheckResult = await preExportCheck(copyOfEventGroup.events);
        if (preCheckResult.err) {
            cancelExport(eventGroupIndex, preCheckResult.eventId);
        } else {
            duration = Math.max(
                duration,
                tcLib.secToFrames(
                    copyOfEventGroup.events[copyOfEventGroup.events.length - 1]
                        .end,
                    trgFrameRate,
                ),
            );

            /* tcOffset */
            if (tcOffset && tcOffset !== "00:00:00:00") {
                if (
                    (tcOffsetType === "subtract" &&
                        tcLib.tcToSec(tcOffset, trgFrameRate, trgDropFrame) <
                            copyOfEventGroup.events[0].start) || tcOffsetType === "add"
                ) {
                    copyOfEventGroup.events.forEach((event, index, events) => {
                        events[index] = offset(
                            event,
                            tcLib.tcToSec(tcOffset, trgFrameRate, trgDropFrame),
                            tcOffsetType,
                        );
                    });
                }
            }

            if (tcMultiplier && tcMultiplier != 0 && tcMultiplier != 1) {
                copyOfEventGroup = tcMultiply(copyOfEventGroup, tcMultiplier);
            }

            options = new defaults.options({
                profile: formats.profileMapping[trgProfile].name,
                formatOptions:
                    formats.profileMapping[trgProfile].options.encode,
                frameRate: trgFrameRate,
                dropFrame: trgDropFrame,
                target_profile: formats.profileMapping[trgProfile].name,
                source_profile: "closedCaptionProject",
                incode: incode || "00:00:00:00",
                window: {
                    width: styleState.width,
                    height: styleState.height,
                    xPadding: styleState.xPadding,
                    yPadding: styleState.yPadding
                }
            });

            if (shouldWatermark(trgExt) || environment.system === "demo") {
                copyOfEventGroup = replaceTrialText(copyOfEventGroup);
            }

            copyOfEventGroup.events.forEach((event, index, events) => {
                events[index].text = wrapStyledTextWithSpan(event.text);
            });
            
            output = await encode(copyOfEventGroup, options);

            if (
                (trgExt === "xml" || trgExt === "ttml") &&
                mxfEmbed &&
                !environment.online &&
                !environment.iframe
            ) {
                // Use Sweetalert to alert the user that they need to be online to export AAF or
                await Swal.fire({
                    ...SWAL_BASE,
                    titleText: m.subtitle_export_aaf_mxf_title(),
                    text: m.subtitle_export_aaf_mxf_offline_text(),
                    confirmButtonText: m.subtitle_export_ok(),
                    allowOutsideClick: false,
                    allowEscapeKey: false,
                    customClass: SWAL_CLASSES.okLight,
                });
            }

            if (
                (trgExt === "xml" || trgExt === "ttml") &&
                mxfEmbed &&
                environment.online
            ) {
                let signedUrl = await uploadTextFileToTemp(output, "xml");
                let mxfUrl = await createMxf(signedUrl, options);
                //download MXF
                let mxfBlob = await fetch(mxfUrl).then((res) => res.blob());
                saveAs(mxfBlob, fileName.replace(".xml", ".mxf"), {
                    autoBom: true,
                });
            } else {
                if (defaultEncoding === "buffer") {
                    fileBlob = output instanceof Blob
                        ? output
                        : new Blob([output], {
                            type: "application/octet-stream",
                        });
                } else if (defaultEncoding.toLowerCase() === "hex") {
                    let byteArray = new Uint8Array(
                        output.match(/.{2}/g).map((e) => parseInt(e, 16)),
                    );
                    fileBlob = new Blob([byteArray], {
                        type: "application/octet-stream",
                    });
                } else if (defaultEncoding.toLowerCase() === "utf-16le") {
                    fileBlob = new Blob([output], {
                        type: "text/plain;charset=utf-16le",
                    });
                } else {
                    fileBlob = new Blob([output], {
                        type: "text/plain;charset=" + fileEncoding,
                    });
                }

                saveAs(fileBlob, fileName, {
                    autoBom: true,
                });
            }

            toast.push(m.subtitle_export_complete_toast(), {
                classes: ["toast-success"],
            });

            localStorage.setItem(
                "cc-subtitle-export-defaults",
                JSON.stringify({
                    trgExt: trgExt,
                    trgProfile: trgProfile,
                }),
            );

            hideModal();
        }
    } catch (e) {
        console.log(e, e.message);
        toast.push(m.subtitle_export_failed_toast({ error: e.message }), {
            classes: ["toast-danger"],
        });
    } finally {
        exporting = false;
    }
}

async function advancedSubtitleExport() {
    exporting = true;
    toast.push(m.subtitle_export_encoding_toast(), { classes: ["toast-info"] });
    try {
        toast.push(m.subtitle_export_complete_toast(), {
            classes: ["toast-success"],
        });

        let options,
            output,
            fileName,
            fileBlob,
            copyOfEventGroups = [],
            eventGroupChannelMappings = {},
            defaultEncoding =
                formats.defaultEncodingMap[
                    formats.profileMapping[trgProfile].name
                ];

        trgFrameRate = parseFloat(trgFrameRate);
        if ([23.976, 24,25,30,50,60].indexOf(trgFrameRate) > -1){
            trgDropFrame = false;
        } else {
            trgDropFrame = trgDropFrame === "true" ? true : false;
        }
        
        trgIncode = tcLib.tcValidate(
            trgIncode,
            trgFrameRate,
            trgDropFrame,
        );

        tcOffset = tcLib.tcValidate(
            tcOffset,
            trgFrameRate,
            trgDropFrame,
        );

        /* Incode Math */
        let incode = trgIncode;

        if (useClockTime === "no"){
            incode = incodeTime.remapIncodeForNonClockPipeline(trgIncode, trgFrameRate, trgDropFrame);
        }
        /* Incode Math End */

        let cc1EventGroup = eventGroupState.find(
            (group) => group.id === cc1EncodeTarget,
        );
        let cc2EventGroup = eventGroupState.find(
            (group) => group.id === cc2EncodeTarget,
        );
        let cc3EventGroup = eventGroupState.find(
            (group) => group.id === cc3EncodeTarget,
        );
        let cc4EventGroup = eventGroupState.find(
            (group) => group.id === cc4EncodeTarget,
        );
        let programAEventGroup = eventGroupState.find(
            (group) => group.id === programAEncodeTarget,
        );
        let programBEventGroup = eventGroupState.find(
            (group) => group.id === programBEncodeTarget,
        );
        let programCEventGroup = eventGroupState.find(
            (group) => group.id === programCEncodeTarget,
        );
        let programDEventGroup = eventGroupState.find(
            (group) => group.id === programDEncodeTarget,
        );
        let programEEventGroup = eventGroupState.find(
            (group) => group.id === programEEncodeTarget,
        );
        let programFEventGroup = eventGroupState.find(
            (group) => group.id === programFEncodeTarget,
        );

        fileName =
            (
                cc1EventGroup ??
                cc2EventGroup ??
                cc3EventGroup ??
                cc4EventGroup ??
                programAEventGroup ??
                programBEventGroup ??
                programBEventGroup ??
                programCEventGroup ??
                programDEventGroup ??
                programEEventGroup ??
                programFEventGroup
            ).name +
            "." +
            trgExt;

        if (cc1EventGroup) {
            copyOfEventGroups.push(JSON.parse(JSON.stringify(cc1EventGroup)));
            eventGroupChannelMappings["cc1"] = copyOfEventGroups.length - 1;
        }

        if (cc2EventGroup) {
            copyOfEventGroups.push(JSON.parse(JSON.stringify(cc2EventGroup)));
            eventGroupChannelMappings["cc2"] = copyOfEventGroups.length - 1;
        }

        if (cc3EventGroup) {
            copyOfEventGroups.push(JSON.parse(JSON.stringify(cc3EventGroup)));
            eventGroupChannelMappings["cc3"] = copyOfEventGroups.length - 1;
        }

        if (cc4EventGroup) {
            copyOfEventGroups.push(JSON.parse(JSON.stringify(cc4EventGroup)));
            eventGroupChannelMappings["cc4"] = copyOfEventGroups.length - 1;
        }

        if (programAEventGroup) {
            copyOfEventGroups.push(
                JSON.parse(JSON.stringify(programAEventGroup)),
            );
            eventGroupChannelMappings["programA"] =
                copyOfEventGroups.length - 1;
        }

        if (programBEventGroup) {
            copyOfEventGroups.push(
                JSON.parse(JSON.stringify(programBEventGroup)),
            );
            eventGroupChannelMappings["programB"] =
                copyOfEventGroups.length - 1;
        }

        if (programCEventGroup) {
            copyOfEventGroups.push(
                JSON.parse(JSON.stringify(programCEventGroup)),
            );
            eventGroupChannelMappings["programC"] =
                copyOfEventGroups.length - 1;
        }

        if (programDEventGroup) {
            copyOfEventGroups.push(
                JSON.parse(JSON.stringify(programDEventGroup)),
            );
            eventGroupChannelMappings["programD"] =
                copyOfEventGroups.length - 1;
        }

        if (programEEventGroup) {
            copyOfEventGroups.push(
                JSON.parse(JSON.stringify(programEEventGroup)),
            );
            eventGroupChannelMappings["programE"] =
                copyOfEventGroups.length - 1;
        }

        if (programFEventGroup) {
            copyOfEventGroups.push(
                JSON.parse(JSON.stringify(programFEventGroup)),
            );
            eventGroupChannelMappings["programF"] =
                copyOfEventGroups.length - 1;
        }

        for (
            let evgIndex = 0;
            evgIndex < copyOfEventGroups.length;
            evgIndex++
        ) {
            let evg = copyOfEventGroups[evgIndex];

            let eventGroupIndex = eventGroupState.findIndex((group) => {
                return group.id === evg.id;
            });

            /* Reposition to Top */
            if (repositionToTop) {
                evg.events.forEach((event) => {
                    event.xPos = "center";
                    event.yPos = "start";
                });
            }
            /* Reposition to Top END */

            /* FS Filtering START */
            if (fns === "exclude") {
                evg.events = evg.events.filter((event) => {
                    return !event.forced;
                });
            } else if (fns === "only") {
                evg.events = evg.events.filter((event) => {
                    return event.forced;
                });
            }
            /* FS Filtering END */

            /* Track Filtering START */
            evg.events = filterEventsByTrack(evg.events, exportTracks);
            /* Track Filtering END */

            /* RTL Formatting */
            if (reverseRtlStartEndOption) {
                evg = reorderRtlText(evg);
            }
            /* RTL Formatting END */

            copyOfEventGroups[evgIndex] = orderByTime(evg);
            copyOfEventGroups[evgIndex] = removeHtmlEntities(evg);
            let preCheckResult = await preExportCheck(evg.events);
            if (preCheckResult.err) {
                cancelExport(eventGroupIndex, preCheckResult.eventId);
            } else {
                duration = Math.max(
                    duration,
                    tcLib.secToFrames(
                        evg.events[evg.events.length - 1].end,
                        trgFrameRate,
                    ),
                );

                /* tcOffset */
                if (tcOffset && tcOffset !== "00:00:00:00") {
                    evg.events.forEach((event, index, events) => {
                        events[index] = offset(
                            event,
                            tcLib.tcToSec(tcOffset, trgFrameRate, trgDropFrame),
                            tcOffsetType,
                        );
                    });
                }

                if (tcMultiplier && tcMultiplier != 0 && tcMultiplier != 1) {
                    evg = tcMultiply(evg, tcMultiplier);
                }

                if (shouldWatermark(trgExt) || environment.system === "demo") {
                    evg = replaceTrialText(evg);
                }

                evg.events.forEach((event, index, events) => {
                    events[index].text = wrapStyledTextWithSpan(event.text);
                });

                copyOfEventGroups[evgIndex] = evg;
            }
        }

        options = new defaults.options({
            profile: formats.profileMapping[trgProfile].name,
            formatOptions: [
                ...formats.profileMapping[trgProfile].options.encode,
                {
                    name: "Event Group Channel Mappings",
                    selected: eventGroupChannelMappings,
                },
                {
                    name: "Program Information",
                    selected: {
                        enable: enableProgramInfo,
                        programName: programName,
                        programLengthHours: programLengthHours,
                        programLengthMinutes: programLengthMinutes,
                        basicKeywordGroup: basicKeywordGroup,
                        detailKeywordGroup: detailKeywordGroup,
                    },
                },
                {
                    name: "V-Chip Information",
                    selected: {
                        enable: enableVChipInfo,
                        type: vChipType,
                        rating: vChipRating,
                        content: vChipContent,
                    },
                },
            ],
            frameRate: trgFrameRate,
            dropFrame: trgDropFrame,
            incode: incode || "00:00:00:00",
            target_profile: formats.profileMapping[trgProfile].name,
            source_profile: "closedCaptionProject",
            window: {
                width: styleState.width,
                height: styleState.height,
                xPadding: styleState.xPadding,
                yPadding: styleState.yPadding
            }
        });

        if (!environment.online && aafEmbed !== "none") {
            // Use Sweetalert to alert the user that they need to be online to export AAF or
            await Swal.fire({
                ...SWAL_BASE,
                titleText: m.subtitle_export_aaf_mxf_title(),
                text: m.subtitle_export_aaf_mxf_offline_text(),
                confirmButtonText: m.subtitle_export_ok(),
                allowOutsideClick: false,
                allowEscapeKey: false,
                customClass: SWAL_CLASSES.okLight,
            });
        }

        // Standard export process for raw files and AAF
        if (trgExt === "raw" && aafEmbed !== "none" && environment.online) {
            if (aafEmbed === "aaf-sc") {
                // For self-contained AAF, create an MCC file instead of RAW
                let mccOptions = new defaults.options({
                    profile: "macCaptionAdvanced",
                    formatOptions: [
                        ...formats.profileMapping["MacCaption 608/708 Advanced"]
                            .options.encode,
                        {
                            name: "Event Group Channel Mappings",
                            selected: eventGroupChannelMappings,
                        },
                        {
                            name: "Program Information",
                            selected: {
                                enable: enableProgramInfo,
                                programName: programName,
                                programLengthHours: programLengthHours,
                                programLengthMinutes: programLengthMinutes,
                                basicKeywordGroup: basicKeywordGroup,
                                detailKeywordGroup: detailKeywordGroup,
                            },
                        },
                        {
                            name: "V-Chip Information",
                            selected: {
                                enable: enableVChipInfo,
                                type: vChipType,
                                rating: vChipRating,
                                content: vChipContent,
                            },
                        },
                    ],
                    frameRate: trgFrameRate,
                    dropFrame: trgDropFrame,
                    incode: "00:00:00:00",
                    target_profile: "macCaptionAdvanced",
                    source_profile: "closedCaptionProject",
                    window: {
                        width: styleState.width,
                        height: styleState.height,
                        xPadding: styleState.xPadding,
                        yPadding: styleState.yPadding
                    }
                });

                // Encode to MCC format
                let mccOutput = await encode(copyOfEventGroups, mccOptions);

                // Upload MCC file to cloud storage
                let signedUrl = await uploadTextFileToTemp(mccOutput, "mcc");

                // Create AAF with embedded MCC
                let aafUrl = await createAaf(signedUrl, {...options, incode : "00:00:00:00"}, aafEmbed, copyOfEventGroups[0].name);

                // Download AAF file
                let aafBlob = await fetch(aafUrl).then((res) => res.blob());
                saveAs(aafBlob, fileName.replace(".raw", ".aaf"), {
                    autoBom: true,
                });
            } else {
                output = await encode(copyOfEventGroups, options);
                // Standard RAW to AAF process
                let byteArray = new Uint8Array(
                    output.match(/.{2}/g).map((e) => parseInt(e, 16)),
                );

                let signedUrl = await uploadRawFileToTemp(byteArray, "raw");
                let aafUrl = await createAaf(signedUrl, {...options, incode : "00:00:00:00"}, aafEmbed);
                //download AAF
                let aafBlob = await fetch(aafUrl).then((res) => res.blob());
                saveAs(
                    aafBlob,
                    fileName.replace(
                        ".raw",
                        "." + (aafEmbed === "aaf" ? "zip" : aafEmbed),
                    ),
                    {
                        autoBom: true,
                    },
                );
            }
        } else {
            // Generate output for the selected format
            output = await encode(copyOfEventGroups, options);

            if (defaultEncoding === "buffer") {
                fileBlob = output instanceof Blob
                    ? output
                    : new Blob([output], {
                        type: "application/octet-stream",
                    });
            } else if (defaultEncoding.toLowerCase() === "hex") {
                let byteArray = new Uint8Array(
                    output.match(/.{2}/g).map((e) => parseInt(e, 16)),
                );
                fileBlob = new Blob([byteArray], {
                    type: "application/octet-stream",
                });
            } else if (defaultEncoding.toLowerCase() === "utf-16le") {
                fileBlob = new Blob([output], {
                    type: "text/plain;charset=utf-16le",
                });
            } else {
                fileBlob = new Blob([output], {
                    type: "text/plain;charset=" + fileEncoding,
                });
            }

            saveAs(fileBlob, fileName, {
                autoBom: true,
            });
        }

        toast.push(m.subtitle_export_complete_toast(), {
            classes: ["toast-success"],
        });

        localStorage.setItem(
            "cc-subtitle-export-defaults",
            JSON.stringify({
                trgExt: trgExt,
                trgProfile: trgProfile,
            }),
        );
    } catch (err) {
        toast.push(m.subtitle_export_failed_toast({ error: err.message }), {
            classes: ["toast-danger"],
        });

        console.log(err);
        console.log(err.message);
    } finally {
        exporting = false;
        hideModal();
    }
}

const uploadRawFileToTemp = async (byteArray, ext) => {
    try {
        let fileName = crypto.randomUUID() + "." + ext;
        let storageRef = ref(storage);

        console.log("Uploading RAW File to TEMP Storage");
        console.log("File Name:", fileName);

        let snapshot = await uploadBytes(ref(storageRef, "/temp/" + fileName), byteArray);
        console.log("Uploaded raw file to temp");
        let sourceUrl = await getDownloadURL(snapshot.ref);
        return sourceUrl;
    } catch (err) {
        console.log(err);
        console.log(err.message);
        throw new Error(
            m.subtitle_export_upload_raw_failed(),
        );
    }
};

const uploadTextFileToTemp = async (output, ext) => {
    try {
        let fileName = crypto.randomUUID() + "." + ext;
        let storageRef = ref(storage);

        console.log("Uploading RAW File to TEMP Storage");
        console.log("File Name:", fileName);

        let snapshot = await uploadString(ref(storageRef, "/temp/" + fileName), output);
        console.log("Uploaded raw file to temp");
        let sourceUrl = await getDownloadURL(snapshot.ref);
        return sourceUrl;
    } catch (err) {
        console.log(err);
        console.log(err.message);
        throw new Error(
            m.subtitle_export_upload_raw_failed(),
        );
    }
};

const createAaf = async (signedUrl, options, target, eventGroupName) => {
    try {
        // console.log("Creating AAF File");
        // console.log("Signed URL:", signedUrl);
        let res = await httpsCallable(functions, "v8EncodeMxf", { timeout: ENCODE_MXF_CALLABLE_TIMEOUT_MS })({
            source: signedUrl,
            frameRate: options.frameRate,
            dropFrame: options.dropFrame,
            duration: Math.round(duration),
            incode: "00:00:00:00",
            name: eventGroupName ? eventGroupName.replace(/\s+/g, '_').replace(/[^\w_]/g, '') : crypto.randomUUID(),
            lang: "eng",
            target: target,
        });

        // console.log(res);
        return res.data;
    } catch (err) {
        console.log(err);
        console.log(err.message);
        throw new Error(m.subtitle_export_create_aaf_failed());
    }
};

const createMxf = async (signedUrl, options) => {
    try {
        // console.log("Creating MXF File");
        // console.log("Signed URL:", signedUrl);
        let res = await httpsCallable(functions, "v8EncodeMxf", { timeout: ENCODE_MXF_CALLABLE_TIMEOUT_MS })({
            source: signedUrl,
            frameRate: options.frameRate,
            dropFrame: options.dropFrame,
            duration: Math.round(duration),
            incode: trgIncode || "00:00:00:00",
            name: crypto.randomUUID(),
            lang: "eng",
            target: "ttmxf",
        });

        // console.log(res);
        return res.data;
    } catch (err) {
        console.log(err);
        console.log(err.message);
        throw new Error(m.subtitle_export_create_aaf_failed());
    }
};
</script>

<div
    transition:fade={{ duration: 100 }}
    class="modal {modalState.selected === 'subtitleExport'
        ? 'show d-block'
        : ''}"
    role="dialog"
    aria-modal="true"
    aria-labelledby="subtitleExportTitle"
    aria-describedby="subtitleExportDesc"
    tabindex="-1">
    <div
        class="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
        <div class="modal-content">
            <div class="modal-header">
                <h4 class="modal-title" id="subtitleExportTitle">
                    {m.subtitle_export_title()}
                </h4>
                <button
                    type="button"
                    class="btn-close"
                    aria-label={m.subtitle_export_close_label()}
                    onclick={hideModal}></button>
            </div>
            <div class="modal-body">
                <p id="subtitleExportDesc" class="small text-muted">
                    <i class="bi bi-lightning-fill" aria-hidden="true"></i>
                    {m.subtitle_export_powered_by()}
                </p>
                <form
                    onsubmit={preventDefault(() =>
                        trgProfile === "Scenarist V1.0 Advanced" ||
                        trgProfile === "MacCaption 608/708 Advanced" ||
                        trgProfile === "608/708 VANC Data"
                            ? advancedSubtitleExport()
                            : exportSubtitles(),
                    )}>
                    <div
                        class="mb-3 {trgProfile === 'Scenarist V1.0 Advanced' ||
                        trgProfile === 'MacCaption 608/708 Advanced' ||
                        trgProfile === '608/708 VANC Data'
                            ? 'd-none'
                            : ''}">
                        <label class="form-label" for="EventGroupSelection"
                            >{m.subtitle_export_event_group_label()}</label>
                        <select
                            class="form-select"
                            bind:value={eventGroup}
                            onchange={syncLanguageCodeFromEventGroup}>
                            {#each eventGroupState as eventGroupOption (eventGroupOption.id)}
                                <option value={eventGroupOption.id}
                                    >{eventGroupOption.name}</option>
                            {/each}
                        </select>
                    </div>
                    <div class="row">
                        <div class="mb-3 col">
                            <label class="form-label" for="targetFileExtension"
                                >{m.subtitle_export_file_extension_label()}</label>
                            <select
                                class="form-select"
                                bind:value={trgExt}
                                onchange={() => {
                                    updateProfile();
                                    warnIfWatermarked(trgExt, showTrialWarning);
                                }}>
                                {#each allowedSubtitleExts(formats.trgExt) as ext (ext)}
                                    <option>{ext}</option>
                                {/each}
                            </select>
                        </div>
                        <div class="mb-3 col">
                            <label
                                class="form-label"
                                for="exampleFormControlSelect1">{m.subtitle_export_profile_label()}</label>
                            <select
                                class="form-select"
                                bind:value={trgProfile}
                                onchange={() => {
                                    updateUseClockTime();
                                    syncLanguageCodeFromEventGroup();
                                }}>
                                {#each formats.trgProfiles[trgExt] as profile (profile)}
                                    <option>{profile}</option>
                                {/each}
                            </select>
                        </div>
                    </div>
                    {#if trgProfile === "608/708 VANC Data"}
                        <!-- AAF EMBED -->
                        <div class="form-check form-check-inline mb-3">
                            <input
                                class="form-check-input"
                                type="radio"
                                name="embedOption"
                                id="embedOptionRadio1"
                                value="aaf"
                                bind:group={aafEmbed} />
                            <label
                                class="form-check-label"
                                for="embedOptionRadio1"
                                >{m.subtitle_export_aaf_embed_linked()}</label>
                        </div>
                        <div class="form-check form-check-inline mb-3">
                            <input
                                class="form-check-input"
                                type="radio"
                                name="embedOption"
                                id="embedOptionRadio4"
                                value="aaf-sc"
                                bind:group={aafEmbed} />
                            <label
                                class="form-check-label"
                                for="embedOptionRadio4"
                                >{m.subtitle_export_aaf_embed_self_contained()}</label>
                        </div>
                        <div class="form-check form-check-inline mb-3">
                            <input
                                class="form-check-input"
                                type="radio"
                                name="embedOption"
                                id="embedOptionRadio2"
                                value="mxf"
                                bind:group={aafEmbed} />
                            <label
                                class="form-check-label"
                                for="embedOptionRadio2">{m.subtitle_export_mxf_embed()}</label>
                        </div>
                        <div class="form-check form-check-inline mb-3">
                            <input
                                class="form-check-input"
                                type="radio"
                                name="embedOption"
                                id="embedOptionRadio3"
                                value="none"
                                bind:group={aafEmbed} />
                            <label
                                class="form-check-label"
                                for="embedOptionRadio3">{m.subtitle_export_none()}</label>
                        </div>
                    {/if}
                    <!-- 708 Options -->
                    {#if trgProfile === "MacCaption 608/708 Advanced" || trgProfile === "608/708 VANC Data"}
                        <button
                            type="button"
                            class="btn btn-outline-warning mb-2 dropdown-toggle w-100"
                            onclick={() => {
                                show708Options = !show708Options;
                            }}>{m.subtitle_export_708_options()}</button>
                        <div class="row">
                            {#if show708Options}
                                <!-- Program A -->
                                <div class="mb-3 col-6">
                                    <label
                                        class="form-label"
                                        for="programAEncodeTarget"
                                        >{m.subtitle_export_program_a()}</label>
                                    <select
                                        class="form-select {programAEncodeTarget ===
                                        'None'
                                            ? 'bg-light'
                                            : ''}"
                                        bind:value={programAEncodeTarget}>
                                        {#each eventGroupState as eventGroupOption (eventGroupOption.id)}
                                            <option value={eventGroupOption.id}
                                                >{eventGroupOption.name}</option>
                                        {/each}
                                        <option>{m.subtitle_export_none()}</option>
                                    </select>
                                </div>
                                <!-- Program B -->
                                <div class="mb-3 col-6">
                                    <label
                                        class="form-label"
                                        for="programBEncodeTarget"
                                        >{m.subtitle_export_program_b()}</label>
                                    <select
                                        class="form-select {programBEncodeTarget ===
                                        'None'
                                            ? 'bg-light'
                                            : ''}"
                                        bind:value={programBEncodeTarget}>
                                        {#each eventGroupState as eventGroupOption (eventGroupOption.id)}
                                            <option value={eventGroupOption.id}
                                                >{eventGroupOption.name}</option>
                                        {/each}
                                        <option>{m.subtitle_export_none()}</option>
                                    </select>
                                </div>
                                <!-- Program C -->
                                <div class="mb-3 col-6">
                                    <label
                                        class="form-label"
                                        for="programCEncodeTarget"
                                        >{m.subtitle_export_program_c()}</label>
                                    <select
                                        class="form-select {programCEncodeTarget ===
                                        'None'
                                            ? 'bg-light'
                                            : ''}"
                                        bind:value={programCEncodeTarget}>
                                        {#each eventGroupState as eventGroupOption (eventGroupOption.id)}
                                            <option value={eventGroupOption.id}
                                                >{eventGroupOption.name}</option>
                                        {/each}
                                        <option>{m.subtitle_export_none()}</option>
                                    </select>
                                </div>
                                <!-- Program D -->
                                <div class="mb-3 col-6">
                                    <label
                                        class="form-label"
                                        for="programDEncodeTarget"
                                        >{m.subtitle_export_program_d()}</label>
                                    <select
                                        class="form-select {programDEncodeTarget ===
                                        'None'
                                            ? 'bg-light'
                                            : ''}"
                                        bind:value={programDEncodeTarget}>
                                        {#each eventGroupState as eventGroupOption (eventGroupOption.id)}
                                            <option value={eventGroupOption.id}
                                                >{eventGroupOption.name}</option>
                                        {/each}
                                        <option>{m.subtitle_export_none()}</option>
                                    </select>
                                </div>
                                <!-- Program E -->
                                <div class="mb-3 col-6">
                                    <label
                                        class="form-label"
                                        for="programEEncodeTarget"
                                        >{m.subtitle_export_program_e()}</label>
                                    <select
                                        class="form-select {programEEncodeTarget ===
                                        'None'
                                            ? 'bg-light'
                                            : ''}"
                                        bind:value={programEEncodeTarget}>
                                        {#each eventGroupState as eventGroupOption (eventGroupOption.id)}
                                            <option value={eventGroupOption.id}
                                                >{eventGroupOption.name}</option>
                                        {/each}
                                        <option>{m.subtitle_export_none()}</option>
                                    </select>
                                </div>
                                <!-- Program F -->
                                <div class="mb-3 col-6">
                                    <label
                                        class="form-label"
                                        for="programFEncodeTarget"
                                        >{m.subtitle_export_program_f()}</label>
                                    <select
                                        class="form-select {programFEncodeTarget ===
                                        'None'
                                            ? 'bg-light'
                                            : ''}"
                                        bind:value={programFEncodeTarget}>
                                        {#each eventGroupState as eventGroupOption (eventGroupOption.id)}
                                            <option value={eventGroupOption.id}
                                                >{eventGroupOption.name}</option>
                                        {/each}
                                        <option>{m.subtitle_export_none()}</option>
                                    </select>
                                </div>
                            {/if}
                        </div>
                    {/if}
                    <!-- 608 Options -->
                    {#if trgProfile === "Scenarist V1.0 Advanced" || trgProfile === "MacCaption 608/708 Advanced" || trgProfile === "608/708 VANC Data"}
                        <button
                            type="button"
                            class="btn btn-outline-warning mb-2 dropdown-toggle w-100"
                            onclick={() => {
                                show608Options = !show608Options;
                            }}>{m.subtitle_export_608_options()}</button>
                        <div class="row">
                            {#if show608Options}
                                <div class="mb-3 col">
                                    <label
                                        class="form-label"
                                        for="cc1EncodeTarget">CC1</label>
                                    <select
                                        class="form-select {cc1EncodeTarget ===
                                        'None'
                                            ? 'bg-light'
                                            : ''}"
                                        bind:value={cc1EncodeTarget}>
                                        {#each eventGroupState as eventGroupOption (eventGroupOption.id)}
                                            <option value={eventGroupOption.id}
                                                >{eventGroupOption.name}</option>
                                        {/each}
                                        <option>{m.subtitle_export_none()}</option>
                                    </select>
                                </div>
                                <div class="mb-3 col">
                                    <label
                                        class="form-label"
                                        for="cc2EncodeTarget">CC2</label>
                                    <select
                                        class="form-select {cc2EncodeTarget ===
                                        'None'
                                            ? 'bg-light'
                                            : ''}"
                                        bind:value={cc2EncodeTarget}>
                                        {#each eventGroupState as eventGroupOption (eventGroupOption.id)}
                                            <option value={eventGroupOption.id}
                                                >{eventGroupOption.name}</option>
                                        {/each}
                                        <option>{m.subtitle_export_none()}</option>
                                    </select>
                                </div>
                                <div class="mb-3 col">
                                    <label
                                        class="form-label"
                                        for="cc3EncodeTarget">CC3</label>
                                    <select
                                        class="form-select {cc3EncodeTarget ===
                                        'None'
                                            ? 'bg-light'
                                            : ''}"
                                        bind:value={cc3EncodeTarget}>
                                        {#each eventGroupState as eventGroupOption (eventGroupOption.id)}
                                            <option value={eventGroupOption.id}
                                                >{eventGroupOption.name}</option>
                                        {/each}
                                        <option>{m.subtitle_export_none()}</option>
                                    </select>
                                </div>
                                <div class="mb-3 col">
                                    <label
                                        class="form-label"
                                        for="cc4EncodeTarget">CC4</label>
                                    <select
                                        class="form-select {cc4EncodeTarget ===
                                        'None'
                                            ? 'bg-light'
                                            : ''}"
                                        bind:value={cc4EncodeTarget}>
                                        {#each eventGroupState as eventGroupOption (eventGroupOption.id)}
                                            <option value={eventGroupOption.id}
                                                >{eventGroupOption.name}</option>
                                        {/each}
                                        <option>{m.subtitle_export_none()}</option>
                                    </select>
                                </div>
                            {/if}
                        </div>
                        <!-- Content Advisory DATA -->
                        <button
                            type="button"
                            class="btn btn-outline-warning mb-2 dropdown-toggle w-100"
                            onclick={() => {
                                showProgramInfoOptions =
                                    !showProgramInfoOptions;
                            }}>{m.subtitle_export_program_info()}</button>
                        <div class="row">
                            {#if showProgramInfoOptions}
                                <div class="mb-3 col-12">
                                    <div class="form-check form-switch">
                                        <input
                                            class="form-check-input"
                                            type="checkbox"
                                            role="switch"
                                            id="enableContentAdvisoryInfoSwitch"
                                            bind:checked={enableProgramInfo} />
                                        <label
                                            class="form-check-label"
                                            for="enableContentAdvisoryInfoSwitch"
                                            >{m.subtitle_export_include_program_info()}</label>
                                    </div>
                                </div>
                                {#if enableProgramInfo}
                                    <div class="mb-3 col-6">
                                        <label
                                            class="form-label"
                                            for="programNameTextField"
                                            >{m.subtitle_export_program_name()}</label>
                                        <input
                                            type="text"
                                            class="form-control"
                                            id="programNameTextField"
                                            bind:value={programName} />
                                    </div>
                                    <div class="col-6">
                                        <label
                                            class="form-label"
                                            for="programLengthInput"
                                            >{m.subtitle_export_program_length()}</label>
                                        <div class="input-group mb-3">
                                            <input
                                                bind:value={programLengthHours}
                                                type="number"
                                                class="form-control"
                                                placeholder={m.subtitle_export_hours_placeholder()}
                                                min="00"
                                                max="23"
                                                step="1"
                                                aria-label={m.subtitle_export_hours_label()} />
                                            <span class="input-group-text"
                                                >:</span>
                                            <input
                                                bind:value={
                                                    programLengthMinutes
                                                }
                                                type="number"
                                                class="form-control"
                                                placeholder={m.subtitle_export_minutes_placeholder()}
                                                min="00"
                                                max="59"
                                                step="1"
                                                aria-label={m.subtitle_export_minutes_label()} />
                                        </div>
                                    </div>
                                    <p class="text-warning lead m-0">
                                        {m.subtitle_export_basic_keyword_group()}
                                    </p>
                                    <div class="col-12 mb-3">
                                        <div
                                            class="form-check form-check-inline">
                                            <input
                                                class="form-check-input"
                                                type="radio"
                                                id="educationCheckbox"
                                                value="education"
                                                bind:group={
                                                    basicKeywordGroup
                                                } />
                                            <label
                                                class="form-check-label"
                                                for="educationCheckbox"
                                                >{m.subtitle_export_keyword_education()}</label>
                                        </div>
                                        <div
                                            class="form-check form-check-inline">
                                            <input
                                                class="form-check-input"
                                                type="radio"
                                                id="entertainmentCheckbox"
                                                value="entertainment"
                                                bind:group={
                                                    basicKeywordGroup
                                                } />
                                            <label
                                                class="form-check-label"
                                                for="entertainmentCheckbox"
                                                >{m.subtitle_export_keyword_entertainment()}</label>
                                        </div>
                                        <div
                                            class="form-check form-check-inline">
                                            <input
                                                class="form-check-input"
                                                type="radio"
                                                id="movieCheckbox"
                                                value="movie"
                                                bind:group={
                                                    basicKeywordGroup
                                                } />
                                            <label
                                                class="form-check-label"
                                                for="movieCheckbox"
                                                >{m.subtitle_export_keyword_movie()}</label>
                                        </div>
                                        <div
                                            class="form-check form-check-inline">
                                            <input
                                                class="form-check-input"
                                                type="radio"
                                                id="newsCheckbox"
                                                value="news"
                                                bind:group={
                                                    basicKeywordGroup
                                                } />
                                            <label
                                                class="form-check-label"
                                                for="newsCheckbox">{m.subtitle_export_keyword_news()}</label>
                                        </div>
                                        <div
                                            class="form-check form-check-inline">
                                            <input
                                                class="form-check-input"
                                                type="radio"
                                                id="religionCheckbox"
                                                value="religion"
                                                bind:group={
                                                    basicKeywordGroup
                                                } />
                                            <label
                                                class="form-check-label"
                                                for="religionCheckbox"
                                                >{m.subtitle_export_keyword_religion()}</label>
                                        </div>
                                        <div
                                            class="form-check form-check-inline">
                                            <input
                                                class="form-check-input"
                                                type="radio"
                                                id="sportsCheckbox"
                                                value="sports"
                                                bind:group={
                                                    basicKeywordGroup
                                                } />
                                            <label
                                                class="form-check-label"
                                                for="sportsCheckbox"
                                                >{m.subtitle_export_keyword_sports()}</label>
                                        </div>
                                        <div
                                            class="form-check form-check-inline">
                                            <input
                                                class="form-check-input"
                                                type="radio"
                                                id="otherCheckbox"
                                                value="other"
                                                bind:group={
                                                    basicKeywordGroup
                                                } />
                                            <label
                                                class="form-check-label"
                                                for="otherCheckbox"
                                                >{m.subtitle_export_keyword_other()}</label>
                                        </div>
                                    </div>
                                    <!-- Detail Keyword Group -->
                                    <p class="text-warning lead m-0">
                                        {m.subtitle_export_detail_keyword_group()} <button
                                            type="button"
                                            class="btn btn-outline-dark btn-sm {detailKeywordGroup.length >
                                            0
                                                ? ''
                                                : 'd-none'}"
                                            style="font-size: 0.7vw;"
                                            onclick={() =>
                                                (detailKeywordGroup = [])}
                                            >{m.subtitle_export_clear_all()}</button>
                                    </p>
                                    <div class="row mb-3">
                                        {#each contentAdvisoryInformation.detail as keyword (keyword)}
                                            <div class="col-3">
                                                <div
                                                    class="form-check form-check-inline">
                                                    <input
                                                        class="form-check-input"
                                                        type="checkbox"
                                                        id="{keyword}Checkbox"
                                                        value={keyword}
                                                        bind:group={
                                                            detailKeywordGroup
                                                        } />
                                                    <label
                                                        class="form-check-label"
                                                        for="{keyword}Checkbox"
                                                        >{keyword}</label>
                                                </div>
                                            </div>
                                        {/each}
                                    </div>
                                {/if}
                            {/if}
                        </div>
                        <!-- Vchip Information-->
                        <button
                            type="button"
                            class="btn btn-outline-warning mb-2 dropdown-toggle w-100"
                            onclick={() => {
                                showVChipOptions = !showVChipOptions;
                            }}>{m.subtitle_export_vchip_info()}</button>
                        <div class="row">
                            {#if showVChipOptions}
                                <div class="mb-3 col-12">
                                    <div class="form-check form-switch">
                                        <input
                                            class="form-check-input"
                                            type="checkbox"
                                            role="switch"
                                            id="enableVChipInfoSwitch"
                                            bind:checked={enableVChipInfo} />
                                        <label
                                            class="form-check-label"
                                            for="enableVChipInfoSwitch"
                                            >{m.subtitle_export_include_vchip_info()}</label>
                                    </div>
                                </div>
                                {#if enableVChipInfo}
                                    <div class="mb-3 col-12">
                                        <label
                                            class="form-label"
                                            for="vchipRatingType">{m.subtitle_export_vchip_type()}</label>
                                        <select
                                            class="form-select"
                                            bind:value={vChipType}>
                                            <option
                                                >{m.subtitle_export_vchip_type_tv()}</option>
                                            <option>{m.subtitle_export_vchip_type_mpaa()}</option>
                                            <option>{m.subtitle_export_vchip_type_ca_en()}</option>
                                            <option>{m.subtitle_export_vchip_type_ca_fr()}</option>
                                        </select>
                                    </div>
                                    <div class="mb-3 col-12">
                                        <label
                                            class="form-label"
                                            for="vchipRating">{m.subtitle_export_vchip_rating()}</label>
                                        <select
                                            class="form-select"
                                            bind:value={vChipRating}
                                            onchange={() => {
                                                vChipContent = [];
                                            }}>
                                            {#each vChipRatings.ratings[vChipType] as rating (rating)}
                                                <option>{rating}</option>
                                            {/each}
                                        </select>
                                    </div>
                                    {#if vChipType === "TV Parental Guideline"}
                                        <div class="col-12">
                                            <p class="mb-1">
                                                {m.subtitle_export_vchip_content_description()}
                                            </p>
                                        </div>

                                        <div class="mb-3 col-3">
                                            <div class="form-check">
                                                <input
                                                    class="form-check-input"
                                                    type="checkbox"
                                                    value="violence"
                                                    disabled={vChipRatings.contentDescriptionMap[
                                                        vChipRating
                                                    ].indexOf("violence") ===
                                                        -1}
                                                    id="violenceContentCheck"
                                                    bind:group={vChipContent} />
                                                <label
                                                    class="form-check-label"
                                                    for="violenceContentCheck">
                                                    {m.subtitle_export_vchip_violence()}
                                                </label>
                                            </div>
                                        </div>
                                        <div class="mb-3 col-3">
                                            <div class="form-check">
                                                <input
                                                    class="form-check-input"
                                                    type="checkbox"
                                                    value="sex"
                                                    disabled={vChipRatings.contentDescriptionMap[
                                                        vChipRating
                                                    ].indexOf("sex") === -1}
                                                    id="sexualContentCheck"
                                                    bind:group={vChipContent} />
                                                <label
                                                    class="form-check-label"
                                                    for="sexualContentCheck">
                                                    {m.subtitle_export_vchip_sexual()}
                                                </label>
                                            </div>
                                        </div>
                                        <div class="mb-3 col-3">
                                            <div class="form-check">
                                                <input
                                                    class="form-check-input"
                                                    type="checkbox"
                                                    value="language"
                                                    id="languageContentCheck"
                                                    disabled={vChipRatings.contentDescriptionMap[
                                                        vChipRating
                                                    ].indexOf("language") ===
                                                        -1}
                                                    bind:group={vChipContent} />
                                                <label
                                                    class="form-check-label"
                                                    for="languageContentCheck">
                                                    {m.subtitle_export_vchip_language()}
                                                </label>
                                            </div>
                                        </div>
                                        <div class="mb-3 col-3">
                                            <div class="form-check">
                                                <input
                                                    class="form-check-input"
                                                    type="checkbox"
                                                    value="dialogue"
                                                    id="dialogueContentCheck"
                                                    disabled={vChipRatings.contentDescriptionMap[
                                                        vChipRating
                                                    ].indexOf("dialogue") ===
                                                        -1}
                                                    bind:group={vChipContent} />
                                                <label
                                                    class="form-check-label"
                                                    for="dialogueContentCheck">
                                                    {m.subtitle_export_vchip_dialogue()}
                                                </label>
                                            </div>
                                        </div>
                                    {/if}
                                {/if}
                            {/if}
                        </div>
                    {/if}
                    <!-- General Options -->
                    <button
                        type="button"
                        class="btn btn-outline-warning mb-2 dropdown-toggle"
                        onclick={() => {
                            showOptions = !showOptions;
                        }}>{m.subtitle_export_more_options()}</button>
                    {#if showOptions}
                        <div
                            id="advancedOptions"
                            class="p-3 bg-secondary shadow rounded">
                            <!-- Advanced Options -->
                            <p class="lead text-warning">{m.subtitle_export_general_options()}</p>
                            <!-- Incode and Clock Time -->
                            <div class="row">
                                <div class="mb-3 col-8">
                                    <label class="form-label" for="trgIncode"
                                        >{m.subtitle_export_incode_label()}</label>
                                    <input
                                        type="text"
                                        class="form-control"
                                        bind:value={trgIncode}
                                        onblur={validateTrgIncode}
                                        onfocus={(e) => {
                                            e.target.select();
                                        }} />
                                </div>

                                <div class="mb-3 col-4">
                                    <label class="form-label" for="useClockTime"
                                        >{m.subtitle_export_use_clock_time()}</label>
                                    <select
                                        class="form-select"
                                        bind:value={useClockTime}>
                                        <option value="yes">{m.subtitle_export_yes()}</option>
                                        <option value="no">{m.subtitle_export_no()}</option>
                                    </select>
                                </div>
                            </div>
                            <!-- Frame Rate + Drop Frame -->
                            <div class="row">
                                <div class="mb-3 col-8">
                                    <label
                                        class="form-label"
                                        for="TrgFrameRateSelect"
                                        >{m.subtitle_export_frame_rate()}</label>
                                    <select
                                        id="TrgFrameRateSelect"
                                        class="form-select"
                                        bind:value={trgFrameRate}
                                        onchange={() => {
                                            if (
                                                parseFloat(trgFrameRate) ===
                                                    29.97 ||
                                                parseFloat(trgFrameRate) ===
                                                    59.94
                                            ) {
                                                trgDropFrame = "true";
                                            } else {
                                                trgDropFrame = "false";
                                            }
                                        }}>
                                        {#each frameRates.frameRates as frameRate (frameRate)}
                                            <option
                                                selected={frameRate ==
                                                    trgFrameRate}
                                                >{frameRate}</option>
                                        {/each}
                                    </select>
                                </div>
                                {#if parseFloat(trgFrameRate) === 29.97 || parseFloat(trgFrameRate) === 59.94}
                                    <div class="mb-3 col-4">
                                        <label
                                            class="form-label"
                                            for="trgDropFrameSelect"
                                            >{m.subtitle_export_drop_frame()}</label>
                                        <select
                                            id="trgDropFrameSelect"
                                            class="form-select"
                                            bind:value={trgDropFrame}>
                                            <option value="true">{m.subtitle_export_yes()}</option>
                                            <option value="false">{m.subtitle_export_no()}</option>
                                        </select>
                                    </div>
                                {/if}
                            </div>
                            <!-- Timecode Offset -->
                            <div class="row">
                                <div class="mb-3 col-8">
                                    <label class="form-label" for="tcOffset"
                                        >{m.subtitle_export_tc_offset()}</label>
                                    <input
                                        type="text"
                                        class="form-control"
                                        bind:value={tcOffset}
                                        onblur={validateTc}
                                        onfocus={(e) => {
                                            e.target.select();
                                        }} />
                                </div>

                                <div class="mb-3 col-4">
                                    <label class="form-label" for="tcOffsetType"
                                        >{m.subtitle_export_offset_type()}</label>
                                    <select
                                        class="form-select"
                                        bind:value={tcOffsetType}>
                                        <option value="add">{m.subtitle_export_offset_add()}</option>
                                        <option value="subtract">{m.subtitle_export_offset_subtract()}</option>
                                    </select>
                                </div>
                            </div>
                            <!-- Timecode Multiplier -->
                            <div class="row">
                                <div class="mb-3 col-8">
                                    <label class="form-label" for="tcMultiplier"
                                        >{m.subtitle_export_tc_multiplier()}</label>
                                    <input
                                        type="number"
                                        class="form-control"
                                        bind:value={tcMultiplier}
                                        onfocus={(e) => {
                                            e.target.select();
                                        }} />
                                </div>
                            </div>
                            <!-- File Encoding -->
                            <div class="mb-3">
                                <label class="form-label" for="TrgFileEncoding"
                                    >{m.subtitle_export_file_encoding()}</label>
                                <select
                                    class="form-select"
                                    bind:value={fileEncoding}>
                                    {#each encodings as encodingOption (encodingOption)}
                                        <option>{encodingOption}</option>
                                    {/each}
                                </select>
                            </div>

                            {#if xmlProfiles.includes(trgProfile)}
                                <!-- MXF Embed -->
                                <div class="mb-3">
                                    <div class="form-check">
                                        <input
                                            class="form-check-input"
                                            type="checkbox"
                                            id="mxfEmbedCheck"
                                            bind:checked={mxfEmbed} />
                                        <label
                                            class="form-check-label"
                                            for="mxfEmbedCheck">
                                            {m.subtitle_export_mxf_wrap()}
                                            <i
                                                class="bi bi-info-circle"
                                                title={m.subtitle_export_mxf_wrap_help()}
                                            ></i>
                                        </label>
                                    </div>
                                </div>
                            {/if}
                            <!-- Reposition to Top -->
                            <div class="mb-3">
                                <p class="text-warning mb-1">{m.subtitle_export_positioning()}</p>
                                <div class="form-check">
                                    <input
                                        class="form-check-input"
                                        type="checkbox"
                                        id="repositionToTopCheck"
                                        bind:checked={
                                            repositionToTop
                                        } />
                                    <label
                                        class="form-check-label"
                                        for="repositionToTopCheck">
                                        {m.subtitle_export_reposition_to_top()}
                                    </label>
                                </div>
                            </div>
                            <!-- RTL Reverse Start/End -->
                            <div class="mb-3">
                                <p class="text-warning mb-1">{m.subtitle_export_rtl_formatting()}</p>
                                <div class="form-check">
                                    <input
                                        class="form-check-input"
                                        type="checkbox"
                                        id="reverseRtlStartEndOptionCheck"
                                        bind:checked={
                                            reverseRtlStartEndOption
                                        } />
                                    <label
                                        class="form-check-label"
                                        for="reverseRtlStartEndOptionCheck">
                                        {m.subtitle_export_reverse_rtl()}
                                    </label>
                                </div>
                            </div>
                            <!-- Forced Subtitles -->
                            <div class="mb-3">
                                <p class="text-warning mb-1">
                                    {m.subtitle_export_forced_subtitles()}
                                </p>
                                <div class="form-check form-check-inline">
                                    <input
                                        class="form-check-input"
                                        type="radio"
                                        name="fnsOption"
                                        id="includeFns"
                                        bind:group={fns}
                                        value="include" />
                                    <label
                                        class="form-check-label"
                                        for="includeFns">{m.subtitle_export_include_fs()}</label>
                                </div>
                                <div class="form-check form-check-inline">
                                    <input
                                        class="form-check-input"
                                        type="radio"
                                        name="fnsOption"
                                        id="excludeFns"
                                        bind:group={fns}
                                        value="exclude" />
                                    <label
                                        class="form-check-label"
                                        for="excludeFns">{m.subtitle_export_exclude_fs()}</label>
                                </div>
                                <div class="form-check form-check-inline">
                                    <input
                                        class="form-check-input"
                                        type="radio"
                                        name="fnsOption"
                                        id="onlyFns"
                                        bind:group={fns}
                                        value="only" />
                                    <label
                                        class="form-check-label"
                                        for="onlyFns">{m.subtitle_export_fs_only()}</label>
                                </div>
                            </div>
                            <!-- Export Tracks -->
                            <div class="mb-3">
                                <label class="form-label text-warning mb-1" for="exportTracksSelect">
                                    {m.export_tracks()}
                                    <i class="bi bi-info-circle" title={m.export_tracks_help()}></i>
                                </label>
                                <select
                                    id="exportTracksSelect"
                                    class="form-select"
                                    multiple
                                    size="5"
                                    bind:value={exportTracks}>
                                    <option value="none">{m.event_track_none()}</option>
                                    {#each TRACK_LETTERS as letter (letter)}
                                        <option value={letter}>{m.multiview_track()} {letter}</option>
                                    {/each}
                                </select>
                            </div>
                            {#if formats.profileMapping[trgProfile].options && formats.profileMapping[trgProfile].options.encode.length > 0}
                                <div class="row border-top border-secondary">
                                    <p class="col-12 lead text-warning pt-2">
                                        {m.subtitle_export_encoding_options()}
                                    </p>
                                    {#each formats.profileMapping[trgProfile].options.encode as encodeOption (encodeOption.name)}
                                        <div class="mb-3 col-6">
                                            <label
                                                class="form-label"
                                                for="decodingOption"
                                                >{encodeOption.name}</label>
                                            {#if encodeOption.type === "textarea"}
                                                <textarea
                                                    class="form-control"
                                                    rows="3"
                                                    bind:value={
                                                        encodeOption.selected
                                                    }></textarea>
                                            {:else if encodeOption.type === "text-input"}
                                                <input
                                                    class="form-control"
                                                    type="text"
                                                    bind:value={
                                                        encodeOption.selected
                                                    } />
                                            {:else if encodeOption.type === "number-input"}
                                                <input
                                                    class="form-control"
                                                    type="number"
                                                    bind:value={
                                                        encodeOption.selected
                                                    } />
                                            {:else}
                                                <select
                                                    class="form-select"
                                                    bind:value={
                                                        encodeOption.selected
                                                    }>
                                                    {#each encodeOption.values as valueOption (valueOption)}
                                                        <option
                                                            >{valueOption}</option>
                                                    {/each}
                                                </select>
                                            {/if}
                                        </div>
                                    {/each}
                                </div>
                            {/if}
                        </div>
                    {/if}
                </form>
            </div>
            <div class="modal-footer">
                {#if exporting}
                    <p>{m.subtitle_export_in_progress()}</p>
                {/if}
                <div class="me-auto d-flex gap-2">
                    <button
                        class="btn btn-outline-secondary"
                        type="button"
                        onclick={showLoadPresetDialog}
                        disabled={exporting}
                        title={m.subtitle_export_load_preset_title()}>
                        <i class="bi bi-folder-open" aria-hidden="true"></i> {m.subtitle_export_load_preset_button()}
                    </button>
                    <button
                        class="btn btn-outline-secondary"
                        type="button"
                        onclick={saveAsPreset}
                        disabled={!eventGroup || exporting}
                        title={m.subtitle_export_save_preset_title()}>
                        <i class="bi bi-save" aria-hidden="true"></i> {m.subtitle_export_save_preset_button()}
                    </button>
                </div>
                <button
                    class="btn btn-primary"
                    type="button"
                    disabled={!eventGroup || exporting}
                    onclick={() =>
                        trgProfile === "Scenarist V1.0 Advanced" ||
                        trgProfile === "MacCaption 608/708 Advanced" ||
                        trgProfile === "608/708 VANC Data"
                            ? advancedSubtitleExport()
                            : exportSubtitles()}>
                    {#if exporting}
                        <div class="float-start p-1">
                            <Circle
                                size="12"
                                color="#ffffff"
                                unit="px"
                                duration="1s"></Circle>
                        </div>
                    {/if}
                    {m.subtitle_export_export_button()}
                </button>
            </div>
        </div>
    </div>
</div>
